import React, { PropsWithChildren, useCallback, useMemo, useRef } from "react";

import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import DropdownPopoverButton from "@src/sharedComponents/DropdownPopoverButton/DropdownPopoverButton";
import Popover from "@src/sharedComponents/Popover/Popover";

import styles from "./CustomDropdownBase.module.css";

interface CurItem {
  value: string;
  displayComponent?: React.ReactNode; // optionally pass in a custom component to display
}

interface Props {
  curItem: CurItem | undefined;
  showDropdown: boolean;
  setShowDropdown: (b: boolean) => void;
  isLoading?: boolean;
  isDisabled: boolean;
  menuWidth?: number;
  width?: number;
  placeholder?: string;
  hideIcon?: boolean;
  className?: string;
  textClassName?: string;
  iconClassName?: string;
  optionsMenuClassName?: string;
}

const CustomDropdownBase: React.FC<PropsWithChildren<Props>> = ({
  curItem,
  showDropdown,
  setShowDropdown,
  isLoading,
  isDisabled,
  menuWidth,
  width,
  placeholder = "Select...",
  hideIcon,
  className,
  textClassName,
  iconClassName,
  optionsMenuClassName,
  children,
}: PropsWithChildren<Props>) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const showOrHidePopover = useCallback(() => {
    if (isDisabled) {
      return;
    }

    setShowDropdown(!showDropdown);
  }, [isDisabled, setShowDropdown, showDropdown]);

  const closeDropdown = useCallback(
    () => setShowDropdown(false),
    [setShowDropdown]
  );

  const displayValue = useMemo(() => {
    if (isLoading) {
      return "Loading...";
    }

    return curItem?.displayComponent ?? curItem?.value;
  }, [isLoading, curItem]);

  return (
    <Popover
      open={showDropdown}
      onClose={closeDropdown}
      anchor={anchorRef.current}
      transformOriginVertical={-10}
      popoverElement={
        <div ref={anchorRef}>
          <DropdownPopoverButton
            displayValue={displayValue}
            onShowPopover={showOrHidePopover}
            className={toClassName(styles.dropdownButton, className)}
            textClassName={toClassName(
              {
                [styles.dropdownButtonTextPlaceholder]: curItem === undefined,
                [styles.dropdownButtonText]: curItem !== undefined,
              },
              textClassName
            )}
            iconClassName={toClassName(
              styles.dropdownButtonIcon,
              iconClassName
            )}
            disabled={isDisabled}
            minWidth={width}
            hideIcon={hideIcon}
            placeholder={placeholder}
          />
        </div>
      }
    >
      <div
        className={toClassName(styles.menu, optionsMenuClassName)}
        style={menuWidth ? { width: `${menuWidth}px` } : {}}
      >
        {children}
      </div>
    </Popover>
  );
};

export default CustomDropdownBase;
